import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, RefreshCw, ArrowLeft } from "lucide-react";
import api from "../../lib/api";
import { AuthShell } from "./AuthShell";
import { OtpInput } from "./OtpVerify";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");   // email | reset | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [expiresIn, setExpiresIn] = useState(600);
  const [resendIn, setResendIn] = useState(60);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/otp/status", { params: { email, purpose: "reset" } });
      if (data.pending) { setExpiresIn(data.expires_in); setResendIn(data.resend_in); }
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (step !== "reset") return;
    loadStatus();
    const id = setInterval(() => {
      setExpiresIn((s) => Math.max(0, s - 1));
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [step]);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a code was sent");
      setStep("reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await api.post("/auth/otp/resend", { email, purpose: "reset" });
      toast.success("A new code was sent");
      loadStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not resend");
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code, password });
      toast.success("Password updated");
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const mm = String(Math.floor(expiresIn / 60)).padStart(2, "0");
  const ss = String(expiresIn % 60).padStart(2, "0");

  if (step === "email") {
    return (
      <AuthShell title="Reset your password" subtitle="We'll email you a 6-digit code to verify it's you">
        <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-form">
          <div>
            <label className="label-xs block mb-2">Registered email</label>
            <input data-testid="forgot-email-input" type="email" required className="input-dark"
                    placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button data-testid="forgot-submit-btn" disabled={busy}
                  className="btn-primary w-full py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send Code"}
          </button>
        </form>
        <p className="text-sm text-gray-500 text-center mt-6">
          Remembered it? <Link data-testid="back-to-login-link" to="/login" className="text-[#00F0FF] hover:underline">Sign in</Link>
        </p>
      </AuthShell>
    );
  }

  if (step === "reset") {
    return (
      <AuthShell title="Reset password" subtitle={<>Enter the code we sent to <span className="text-white">{email}</span></>}>
        <form onSubmit={savePassword} className="space-y-5" data-testid="reset-form">
          <div className="flex items-center justify-center mb-1">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#7000FF]/15 border border-[#7000FF]/30">
              <KeyRound className="w-6 h-6 text-[#7000FF]" />
            </div>
          </div>
          <OtpInput value={code} onChange={setCode} testIdPrefix="reset-otp" />
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Code expires in <span className="tabular text-[#00F0FF]">{mm}:{ss}</span></span>
            <button type="button" onClick={resend} disabled={resendIn > 0}
                    data-testid="reset-resend-btn"
                    className="flex items-center gap-1 text-[#00F0FF] disabled:opacity-40 disabled:cursor-not-allowed">
              <RefreshCw size={12} /> {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
            </button>
          </div>
          <div>
            <label className="label-xs block mb-2">New password</label>
            <input data-testid="new-password-input" type="password" required minLength={8}
                    className="input-dark" placeholder="Min. 8 characters"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <label className="label-xs block mb-2">Confirm password</label>
            <input data-testid="confirm-password-input" type="password" required minLength={8}
                    className="input-dark" placeholder="Repeat the password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep("email")}
                    className="btn-outline px-4 py-2.5 text-sm flex items-center gap-1">
              <ArrowLeft size={14} /> Back
            </button>
            <button data-testid="reset-submit-btn" disabled={busy}
                    className="btn-primary flex-1 py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save New Password"}
            </button>
          </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Password updated" subtitle="You can now sign in with the new password">
      <div className="text-center py-4" data-testid="reset-success">
        <CheckCircle2 className="w-14 h-14 text-[#00E676] mx-auto mb-4" />
        <p className="text-sm text-gray-400 mb-6">Your password has been reset successfully.</p>
        <button onClick={() => navigate("/login")} className="btn-primary px-6 py-2.5 text-sm">Back to Sign In</button>
      </div>
    </AuthShell>
  );
}
